import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import LanguageDetector from 'i18next-browser-languagedetector';
import HttpBackend from 'i18next-http-backend';

// Bundle English as fallback for offline first-load
import enTranslation from '@/locales/en/translation.json';

export const languages = [
  { code: 'en', name: 'English', nativeName: 'English', fontFamily: 'font-sans', locale: 'en-US' },
  { code: 'my', name: 'Burmese', nativeName: 'မြန်မာ', fontFamily: 'font-myanmar', locale: 'my-MM' },
  { code: 'bn', name: 'Bengali', nativeName: 'বাংলা', fontFamily: 'font-bengali', locale: 'bn-BD' },
  { code: 'sw', name: 'Swahili', nativeName: 'Kiswahili', fontFamily: 'font-sans', locale: 'sw-TZ' },
  { code: 'uk', name: 'Ukrainian', nativeName: 'Українська', fontFamily: 'font-sans', locale: 'uk-UA' },
  { code: 'lo', name: 'Lao', nativeName: 'ລາວ', fontFamily: 'font-lao', locale: 'lo-LA' },
  
  { code: 'vi', name: 'Vietnamese', nativeName: 'Tiếng Việt', fontFamily: 'font-sans', locale: 'vi-VN' },
  { code: 'tl', name: 'Tagalog', nativeName: 'Tagalog', fontFamily: 'font-sans', locale: 'tl-PH' },
  { code: 'ur', name: 'Urdu', nativeName: 'اردو', fontFamily: 'font-urdu', locale: 'ur-PK', dir: 'rtl' },
  { code: 'si', name: 'Sinhala', nativeName: 'සිංහල', fontFamily: 'font-sans', locale: 'si-LK' },
  { code: 'ta', name: 'Tamil', nativeName: 'தமிழ்', fontFamily: 'font-sans', locale: 'ta-LK' },
  { code: 'am', name: 'Amharic', nativeName: 'አማརኛ', fontFamily: 'font-sans', locale: 'am-ET' },
  { code: 'km', name: 'Khmer', nativeName: 'ខ្មែរ', fontFamily: 'font-sans', locale: 'km-KH' },
  // FIX-23: AM/AZ/UZ cast languages (hy needs Armenian glyph coverage; az/uz are Latin)
  { code: 'hy', name: 'Armenian', nativeName: 'Հայերեն', fontFamily: 'font-armenian', locale: 'hy-AM' },
  { code: 'az', name: 'Azerbaijani', nativeName: 'Azərbaycanca', fontFamily: 'font-sans', locale: 'az-AZ' },
  { code: 'uz', name: 'Uzbek', nativeName: 'Oʻzbekcha', fontFamily: 'font-sans', locale: 'uz-UZ' },
];

export const getLanguageFont = (languageCode: string): string => {
  const language = languages.find(lang => lang.code === languageCode);
  return language?.fontFamily || 'font-sans';
};

// Pre-cache user's selected language in service worker
export const precacheLanguage = async (languageCode: string) => {
  if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
    navigator.serviceWorker.controller.postMessage({
      type: 'CACHE_LANGUAGE',
      language: languageCode,
    });
  }
};

// Lazy load resources for dev tools (translation checker)
// Only loaded when needed in development
export const loadAllResources = async () => {
  const [bn, my, sw, uk, lo, vi, tl, ur, si, ta, am, km] = await Promise.all([
    import('@/locales/bn/translation.json'),
    import('@/locales/my/translation.json'),
    import('@/locales/sw/translation.json'),
    import('@/locales/uk/translation.json'),
    import('@/locales/lo/translation.json'),
    import('@/locales/vi/translation.json'),
    import('@/locales/tl/translation.json'),
    import('@/locales/ur/translation.json'),
    import('@/locales/si/translation.json'),
    import('@/locales/ta/translation.json'),
    import('@/locales/am/translation.json'),
    import('@/locales/km/translation.json'),
  ]);
  const [hy, az, uz] = await Promise.all([
    import('@/locales/hy/translation.json'),
    import('@/locales/az/translation.json'),
    import('@/locales/uz/translation.json'),
  ]);

  return {
    en: { translation: enTranslation },
    bn: { translation: bn.default },
    my: { translation: my.default },
    sw: { translation: sw.default },
    uk: { translation: uk.default },
    lo: { translation: lo.default },

    vi: { translation: vi.default },
    tl: { translation: tl.default },
    ur: { translation: ur.default },
    si: { translation: si.default },
    ta: { translation: ta.default },
    am: { translation: am.default },
    km: { translation: km.default },
    hy: { translation: hy.default },
    az: { translation: az.default },
    uz: { translation: uz.default },
  };
};

const customBackendLoadPath = (lng: string, ns: string) => {
  return `/locales/${lng}/translation.json`;
};

i18n
  .use(HttpBackend)
  .use(LanguageDetector)
  .use(initReactI18next)
  .init({
    fallbackLng: 'en',
    debug: import.meta.env.DEV,
    interpolation: {
      escapeValue: false,
    },
    detection: {
      order: ['localStorage', 'navigator'],
      caches: ['localStorage'],
    },
    backend: {
      loadPath: customBackendLoadPath,
    },
    // Bundle English for immediate offline use
    partialBundledLanguages: true,
    resources: {
      en: { translation: enTranslation },
    },
    // React suspense integration
    react: {
      useSuspense: false,
    },
  });

// Pre-cache the detected/saved language on init
const savedLanguage = localStorage.getItem('i18nextLng');
if (savedLanguage && savedLanguage !== 'en') {
  precacheLanguage(savedLanguage);
}

export default i18n;